import { forwardRef, KeyboardEventHandler, ReactNode } from 'react';
import {
  NumberInput as MantineNumberInput,
  NumberInputProps as MantineNumberInputProps,
} from '@mantine/core';
import clsx from 'clsx';

import { ClearButton } from '../clearbutton/ClearButton';
import { FormLabel } from '../formlabel/FormLabel';
import { LoadingIndicator } from '../loading-indicator/LoadingIndicator';

import inputStyles from '../input/input.module.scss';

const MAXIMUM = 999_999_999_999.999;
const MINIMUM = -MAXIMUM;
const MAX_DECIMAL_SCALE = 3;

export type NumberInputProps = {
  allowDecimal?: boolean;
  clampBehaviour?: MantineNumberInputProps['clampBehavior'];
  clearable?: boolean;
  decimalScale?: number;
  disabled?: boolean;
  error?: string;
  factor?: number;
  id?: string;
  inlineError?: boolean;
  label?: ReactNode | ReactNode[];
  loading?: boolean;
  max?: number;
  min?: number;
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  suffix?: string;
  tableLayout?: boolean;
  testId?: string;
  value?: number | string;
  onBlur?: () => void;
  onChange?: (value: string | number) => void;
  onClear?: () => void;
  onKeyUp?: KeyboardEventHandler;
};

const getDecimalSeparatorForLocale = (): string | undefined => {
  return new Intl.NumberFormat([navigator.language])
    .formatToParts(1.1)
    .find((part) => part.type === 'decimal')?.value;
};

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  function NumberInput(
    {
      allowDecimal,
      clampBehaviour = 'strict',
      clearable,
      decimalScale = MAX_DECIMAL_SCALE,
      disabled,
      error,
      factor = 1,
      id,
      inlineError,
      label,
      loading,
      max = MAXIMUM,
      min = MINIMUM,
      placeholder,
      readOnly = false,
      required,
      suffix,
      tableLayout = false,
      testId,
      value,
      onBlur,
      onChange,
      onClear,
      onKeyUp,
    },
    ref
  ) {
    const showClearAction = value != null && !loading && !disabled;

    let rightSection: ReactNode | undefined;
    if (loading) {
      rightSection = <LoadingIndicator data-testid="input-loading-indicator" />;
    } else if (clearable) {
      rightSection = (
        <ClearButton
          hidden={!showClearAction}
          disabled={disabled || readOnly}
          onClear={onClear}
        />
      );
    }

    return (
      <div
        className={clsx({ [inputStyles['root--table-layout']]: tableLayout })}
      >
        <FormLabel
          id={id}
          required={required}
          disabled={disabled}
          loading={loading}
          error={error || undefined}
          inline={inlineError}
          label={label}
        />
        <MantineNumberInput
          unstyled
          allowDecimal={allowDecimal}
          data-testid={testId}
          decimalScale={Math.min(decimalScale, MAX_DECIMAL_SCALE)}
          clampBehavior={clampBehaviour}
          classNames={{
            wrapper: inputStyles['wrapper'],
            input: clsx(inputStyles['input'], {
              [inputStyles['input--error']]: error != null,
            }),
            section: inputStyles['section'],
          }}
          max={Math.min(max, MAXIMUM)}
          min={Math.max(min, MINIMUM)}
          disabled={disabled || loading}
          id={id}
          placeholder={placeholder}
          hideControls={true}
          decimalSeparator={getDecimalSeparatorForLocale()}
          readOnly={readOnly}
          rightSection={rightSection}
          rightSectionPointerEvents={showClearAction ? 'auto' : 'none'}
          onBlur={onBlur}
          onChange={(value) => {
            onChange?.(
              typeof value === 'number' && factor ? value / factor : value
            );
          }}
          onKeyUp={onKeyUp}
          ref={ref}
          suffix={suffix}
          value={
            typeof value === 'number' && factor ? value * factor : (value ?? '')
          }
        />
      </div>
    );
  }
);
